import { useMemo, useCallback } from 'react';
import { sumBy } from 'lodash';

/**
 * Custom hook for schema-driven form validation.
 * Reads formSchema.validation.requiredFields and checks each against current props/state.
 *
 * @param {Object} formSchema - The dynamic form schema
 * @param {Object} props - Props from ManagedProductConfig
 * @param {Object} dynamicState - Dynamic state managed by DynamicConfigForm
 * @returns {{ allRequired: boolean, validateForSave: Function }}
 */
export function useDynamicValidation(formSchema, props, dynamicState) {
  const requiredFields = useMemo(() => formSchema?.validation?.requiredFields || [], [formSchema]);
  const credentialSchema = formSchema?.credentials;

  const allRequired = useMemo(() => {
    return requiredFields.every(field => checkField(field, props, dynamicState, credentialSchema));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.fields,
    props.bugsDateBack,
    props.priorityMaps,
    props.vendorStatuses,
    props.vendorEventScopes,
    props.vendorBugServices,
    props.vendorBugCategories,
    props.vendorBugTypes,
    props.vendorImpactedRegions,
    props.snCompanySysId,
    props.bugRisk,
    dynamicState,
    requiredFields,
    credentialSchema
  ]);

  /**
   * Validates form state for save and returns the accordion section to open on failure.
   * Section IDs: 'creds', 'servicenow', 'filters', 'schedule', 'ai'
   * @param {Object} options
   * @param {boolean} options.isValidCredentials - Whether credentials have been validated
   * @param {Array} options.bugRisk - Current bug risk values
   * @returns {{ valid: boolean, section: string|null, redirectTab: string|null, error: string|null }}
   */
  const validateForSave = useCallback(
    ({ isValidCredentials, bugRisk }) => {
      // Check credentials
      if (formSchema?.tabs?.credentials && credentialSchema) {
        const storedField = credentialSchema.storedCheckField;
        const hasStoredCreds = storedField && props.fields?.[storedField];
        if (!isValidCredentials && !hasStoredCreds) {
          return { valid: false, section: 'creds', redirectTab: '2', error: 'Credentials Test Missing.' };
        }

        // Check post-credential required widgets only when credentials are freshly validated.
        // When stored creds exist (re-saving an existing config), skip this check — the
        // previously-saved values are already included in the save payload via resolveSource.
        if (isValidCredentials) {
          const widgets = credentialSchema.postCredentialWidgets || [];
          for (const widget of widgets) {
            if (widget.required && isPostCredWidgetConditionMet(widget, dynamicState, props)) {
              const val = dynamicState[widget.stateKey] || props[widget.stateKey];
              if (
                !val ||
                (Array.isArray(val) && !val.length) ||
                (!Array.isArray(val) && typeof val === 'object' && !val.value)
              ) {
                return {
                  valid: false,
                  section: 'creds',
                  redirectTab: '2',
                  error: `Please complete all required fields.`
                };
              }
            }
          }
        }
      }

      // Vendors with aiRequired cannot run at all without AI (the state machine
      // terminates at initSetup), so refuse to save a configuration that could
      // never execute. Checked before the ServiceNow block because AI mode
      // determines which priority option set is valid for vendors with
      // aiTogglesPriorityType — asking the user to remap priorities before
      // asking them to enable AI would be backwards.
      // Gates on vendorAiEnabled (vendorAiSettings.aiEnabled only), not the
      // effective aiEnabled/enableChatGptPriority (vendor AND global). This
      // deliberately matches the backend's gate (init_setup/base.py and
      // health_check/base.py), which reads only vendorAiSettings.aiEnabled and
      // never looks at Global AI, so Global AI being off does not block saving
      // here even though it does suppress ChatGPT priority features elsewhere.
      // If the AI settings fetch fails, aiRequired/vendorAiEnabled are reset to
      // false on vendor switch (see ManagedProductConfig's vendor-change
      // effect), so a stale value from a previous vendor can't leak in and
      // wrongly block (or permit) saving.
      if (props.aiRequired && !props.vendorAiEnabled) {
        return {
          valid: false,
          section: 'ai',
          redirectTab: '2',
          error: 'AI is required for this vendor — enable AI in the AI Settings section before saving.'
        };
      }

      // Check ServiceNow settings: snCompanySysId + priorityMaps
      if (!props.snCompanySysId?.value) {
        return { valid: false, section: 'servicenow', redirectTab: '2', error: null };
      }

      if (
        requiredFields.includes('priorityMaps') &&
        !checkField('priorityMaps', props, dynamicState, credentialSchema)
      ) {
        return { valid: false, section: 'servicenow', redirectTab: '2', error: null };
      }

      // Validate all priority maps have both sides filled
      if (props.priorityMaps?.length) {
        const allValid = props.priorityMaps.every(m => m.vendorPriority && m.snPriority);
        if (!allValid) {
          return { valid: false, section: 'servicenow', redirectTab: '2', error: 'Invalid priorities' };
        }
      }

      // Schema-driven: when AI mode toggles the priority option set, the
      // saved priorityMaps' isCustom flag must match the current aiEnabled.
      // Catches stale or mismatched state (including pre-existing inconsistent
      // records) and forces remap before save.
      if (formSchema?.priorityConfig?.aiTogglesPriorityType && props.priorityMaps?.length) {
        const aiOn = !!props.aiEnabled;
        const mismatch = props.priorityMaps.some(m => !!m.vendorPriority?.isCustom !== aiOn);
        if (mismatch) {
          return {
            valid: false,
            section: 'servicenow',
            redirectTab: '2',
            error: aiOn
              ? 'AI is enabled — all priority mappings must use AI priorities. Please remap.'
              : 'AI is disabled — priority mappings must use the standard priorities. Please remap.'
          };
        }
      }

      // Check filter fields: bugsDateBack + all filter-related required fields
      if (!props.bugsDateBack?.length) {
        return { valid: false, section: 'filters', redirectTab: '2', error: null };
      }

      const filterFields = [
        'vendorStatuses',
        'vendorBugServices',
        'vendorBugCategories',
        'vendorBugTypes',
        'vendorProductBugCategories',
        'vendorEventScopes',
        'vendorImpactedRegions'
      ];
      for (const field of requiredFields) {
        if (filterFields.includes(field) && !checkField(field, props, dynamicState, credentialSchema)) {
          return { valid: false, section: 'filters', redirectTab: '2', error: null };
        }
      }

      // Check credential field values for secret save
      if (formSchema?.tabs?.credentials && credentialSchema) {
        const storedField = credentialSchema.storedCheckField;
        if (!props.fields?.[storedField]) {
          const credFields = credentialSchema.fields || [];
          const allFilled = credFields.every(f => !f.required || props.fields?.[f.id]);
          if (!allFilled) {
            return { valid: false, section: 'creds', redirectTab: '2', error: null };
          }
        }
      }

      // Check risk score (Tab 5)
      if (formSchema?.tabs?.riskScore && bugRisk) {
        const activeRisk = bugRisk.filter(y => y.active);
        const activeSum = sumBy(activeRisk, risk => risk.value);
        if (activeSum < 100 && activeSum > 1) {
          return { valid: false, redirectTab: '5', section: null, error: 'Bug risk share total must be 100' };
        }
      }

      return { valid: true, redirectTab: null, section: null, error: null };
    },
    [formSchema, credentialSchema, props, dynamicState, requiredFields]
  );

  return { allRequired, validateForSave };
}

/**
 * Checks whether a post-credential widget's condition is satisfied.
 * Supports string conditions (stateKey truthy check) and object conditions
 * ({ stateKey, equals } for equality checks).
 */
function isPostCredWidgetConditionMet(widget, dynamicState, props) {
  const condition = widget.condition;
  if (!condition) return true;
  const conditions = Array.isArray(condition) ? condition : [condition];
  return conditions.every(cond => {
    if (typeof cond === 'string') {
      const val = dynamicState[cond] !== undefined ? dynamicState[cond] : props[cond];
      return !!val;
    }
    if (typeof cond === 'object' && cond.stateKey) {
      const val = dynamicState[cond.stateKey] !== undefined ? dynamicState[cond.stateKey] : props[cond.stateKey];
      if (cond.equals !== undefined) return val === cond.equals;
      return !!val;
    }
    return true;
  });
}

/**
 * Checks whether a single required field is satisfied.
 */
function checkField(field, props, dynamicState, credentialSchema) {
  switch (field) {
    case 'bugsDateBack':
      return !!props.bugsDateBack?.length;
    case 'snCompanySysId':
      return !!props.snCompanySysId?.value;
    case 'priorityMaps':
      return !!props.priorityMaps?.length && !props.priorityMaps.some(m => !m.snPriority || !m.vendorPriority);
    case 'vendorStatuses':
      return !!props.vendorStatuses?.length;
    case 'vendorEventScopes':
      return !!props.vendorEventScopes?.length;
    case 'vendorBugServices':
      return !!props.vendorBugServices?.length;
    case 'vendorBugCategories':
      return !!props.vendorBugCategories?.length;
    case 'vendorBugTypes':
      return !!props.vendorBugTypes?.length;
    case 'vendorImpactedRegions':
      return !!props.vendorImpactedRegions?.length;
    case 'credentials': {
      if (!credentialSchema) return true;
      const storedField = credentialSchema.storedCheckField;
      if (storedField && props.fields?.[storedField]) return true;
      const credFields = credentialSchema.fields || [];
      return credFields.every(f => !f.required || props.fields?.[f.id]);
    }
    default: {
      // Check dynamicState, then props
      const val = dynamicState[field] !== undefined ? dynamicState[field] : props[field];
      if (val === null || val === undefined) return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === 'object') return !!val.value;
      return !!val;
    }
  }
}
